import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface IndiaOpsTabProps {
  bookingId: number;
  onRefresh?: () => void;
}

interface Collection {
  id?: number;
  payment_date: string;
  amount_inr: number;
  payment_method: string;
  payment_reference: string;
  remarks: string;
}

interface Remittance {
  id?: number;
  remittance_date: string;
  amount_usd: number;
  amount_inr: number;
  conversion_rate: number;
  remittance_ref: string;
  customer_pan: string;
  status: string;
  remarks: string;
}

interface GstGovtPayment {
  id?: number;
  period: string;
  total_amount: number;
  remittance_date: string;
  challan_number: string;
  status: string;
  remarks: string;
}

const INDIA_PAYMENT_METHODS = [
  "Wynder - Fed",
  "Biji Mathew - SBI Pampady(Current)",
];

const IndiaOpsTab: React.FC<IndiaOpsTabProps> = ({ bookingId, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // GST invoice summary (read from unified endpoint)
  const [gstSummary, setGstSummary] = useState<any>(null);
  const [customerPan, setCustomerPan] = useState('');
  const [gstStatus, setGstStatus] = useState('DRAFT');

  // Collections
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionsTotal, setCollectionsTotal] = useState(0);

  // Remittances
  const [remittances, setRemittances] = useState<Remittance[]>([]);
  const [remittedTotal, setRemittedTotal] = useState(0);

  // GST govt
  const [gstGovtPayments, setGstGovtPayments] = useState<GstGovtPayment[]>([]);
  const [gstRemittedTotal, setGstRemittedTotal] = useState(0);

  // New entry forms
  const [newCollection, setNewCollection] = useState<Collection>({ payment_date: new Date().toISOString().split('T')[0], amount_inr: 0, payment_method: '', payment_reference: '', remarks: '' });
  const [newRemittance, setNewRemittance] = useState<Remittance>({ remittance_date: new Date().toISOString().split('T')[0], amount_usd: 0, amount_inr: 0, conversion_rate: 0, remittance_ref: '', customer_pan: '', status: 'PENDING', remarks: '' });
  const [newGstGovt, setNewGstGovt] = useState<GstGovtPayment>({ period: '', total_amount: 0, remittance_date: '', challan_number: '', status: 'PENDING', remarks: '' });

  const [showAddCollection, setShowAddCollection] = useState(false);
  const [showAddRemittance, setShowAddRemittance] = useState(false);
  const [showAddGstGovt, setShowAddGstGovt] = useState(false);
  const [panFileUrl, setPanFileUrl] = useState<string | null>(null);
  const [uploadingPan, setUploadingPan] = useState(false);

  useEffect(() => { loadData(); }, [bookingId]);

  const getToken = () => sessionStorage.getItem('jwt_token');

  const loadData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };

      const [unifiedRes, collectionsRes, remittancesRes, gstGovtRes] = await Promise.all([
        fetch(`/api/india-ops/${bookingId}/unified`, { headers }),
        fetch(`/api/india-ops/${bookingId}/collections`, { headers }),
        fetch(`/api/india-ops/${bookingId}/remittances`, { headers }),
        fetch(`/api/india-ops/${bookingId}/gst-remittances`, { headers }),
      ]);

      const unified = await unifiedRes.json();
      const colData = await collectionsRes.json();
      const remData = await remittancesRes.json();
      const govtData = await gstGovtRes.json();

      if (unified.success && unified.data.gst_invoice) {
        setGstSummary(unified.data.gst_invoice);
        setCustomerPan(unified.data.gst_invoice.customer_pan || '');
        setGstStatus(unified.data.gst_invoice.status || 'DRAFT');
        setPanFileUrl(unified.data.gst_invoice.pan_file_url || null);
      }

      if (colData.success) {
        setCollections(colData.data);
        setCollectionsTotal(colData.data.reduce((s: number, c: any) => s + c.amount_inr, 0));
      }
      if (remData.success) {
        setRemittances(remData.data);
        setRemittedTotal(remData.data.filter((r: any) => r.status === 'COMPLETED').reduce((s: number, r: any) => s + r.amount_usd, 0));
      }
      if (govtData.success) {
        setGstGovtPayments(govtData.data);
        setGstRemittedTotal(govtData.data.filter((g: any) => g.status === 'PAID').reduce((s: number, g: any) => s + g.total_amount, 0));
      }
    } catch (error) {
      console.error('Error loading India ops data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCollection = async () => {
    if (!newCollection.amount_inr || !newCollection.payment_date) { alert('Date and amount required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/india-ops/${bookingId}/collections`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(newCollection),
      });
      const data = await res.json();
      if (data.success) {
        setShowAddCollection(false);
        setNewCollection({ payment_date: new Date().toISOString().split('T')[0], amount_inr: 0, payment_method: '', payment_reference: '', remarks: '' });
        loadData();
      } else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error saving collection'); }
    finally { setSaving(false); }
  };

  const deleteCollection = async (id: number) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      const res = await fetch(`/api/india-ops/${bookingId}/collections/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) loadData();
    } catch (e) { alert('Error deleting'); }
  };

  const saveRemittance = async () => {
    if (!newRemittance.amount_usd || !newRemittance.remittance_date) { alert('Date and USD amount required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/india-ops/${bookingId}/remittances`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ ...newRemittance, customer_pan: customerPan }),
      });
      const data = await res.json();
      if (data.success) {
        setShowAddRemittance(false);
        setNewRemittance({ remittance_date: new Date().toISOString().split('T')[0], amount_usd: 0, amount_inr: 0, conversion_rate: 0, remittance_ref: '', customer_pan: '', status: 'PENDING', remarks: '' });
        loadData();
      } else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error saving remittance'); }
    finally { setSaving(false); }
  };

  const updateRemittanceStatus = async (id: number, status: string) => {
    try {
      await fetch(`/api/india-ops/${bookingId}/remittances/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ status }),
      });
      loadData();
    } catch (e) { alert('Error updating status'); }
  };

  const saveGstGovt = async () => {
    if (!newGstGovt.total_amount) { alert('Amount required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`/api/india-ops/${bookingId}/gst-remittances`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify(newGstGovt),
      });
      const data = await res.json();
      if (data.success) {
        setShowAddGstGovt(false);
        setNewGstGovt({ period: '', total_amount: 0, remittance_date: '', challan_number: '', status: 'PENDING', remarks: '' });
        loadData();
      } else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error saving GST remittance'); }
    finally { setSaving(false); }
  };

  const updateGstStatus = async (newStatus: string) => {
    try {
      await fetch(`/api/india-ops/${bookingId}/gst-status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ status: newStatus, customer_pan: customerPan }),
      });
      setGstStatus(newStatus);
    } catch (e) { alert('Error updating GST status'); }
  };

  const savePan = async () => {
    try {
      await fetch(`/api/india-ops/${bookingId}/gst-status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({ customer_pan: customerPan }),
      });
      alert('PAN saved');
    } catch (e) { alert('Error saving PAN'); }
  };

  const uploadPanFile = async (file: File) => {
    setUploadingPan(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/india-ops/${bookingId}/pan-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setPanFileUrl(data.file_url);
        alert('PAN card uploaded');
      } else { alert('Error: ' + (data.detail || data.message)); }
    } catch (e) { alert('Error uploading PAN card'); }
    finally { setUploadingPan(false); }
  };

  const deletePanFile = async () => {
    if (!window.confirm('Delete PAN card file?')) return;
    try {
      const res = await fetch(`/api/india-ops/${bookingId}/pan-file`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) { setPanFileUrl(null); }
    } catch (e) { alert('Error deleting'); }
  };

  if (loading) return <div className="text-center py-8">Loading India Operations...</div>;

  if (!gstSummary) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg mb-2">No GST invoice found for this booking.</p>
        <p className="text-sm">Create a GST invoice first from the GST Invoice tab.</p>
      </div>
    );
  }

  const gstInvoiceTotal = gstSummary.total_inr || 0;
  const collectionBalance = gstInvoiceTotal - collectionsTotal;
  const packageCostUsd = gstSummary.package_cost_usd || 0;
  const remittanceBalance = packageCostUsd - remittedTotal;
  const gstCollected = gstSummary.gst_amount || 0;
  const gstOutstanding = gstCollected - gstRemittedTotal;

  return (
    <div className="space-y-6">
      {/* GST Invoice Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-blue-800">
            {gstSummary.gst_invoice_number
              ? `GST Invoice: ${gstSummary.gst_invoice_number}`
              : `Proforma: ${gstSummary.proforma_number || '—'} (not yet issued as Tax Invoice)`}
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            <Select value={gstStatus} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateGstStatus(e.target.value)} className="text-xs w-32">
              <option value="DRAFT">Draft</option>
              <option value="ISSUED">Issued</option>
              <option value="DECLARED">Declared</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">Package Cost:</span><div className="font-medium">₹{gstSummary.package_cost_inr?.toLocaleString('en-IN')}</div></div>
          <div><span className="text-gray-500">Handling Fee:</span><div className="font-medium text-green-700">₹{gstSummary.handling_fee_inr?.toLocaleString('en-IN')}</div></div>
          <div><span className="text-gray-500">GST:</span><div className="font-medium text-blue-700">₹{gstSummary.gst_amount?.toLocaleString('en-IN')}</div></div>
          <div><span className="text-gray-500">Total Invoice:</span><div className="font-bold">₹{gstInvoiceTotal.toLocaleString('en-IN')}</div></div>
        </div>
      </div>

      {/* Agent Collections */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">💰 Agent Collections (INR)</h4>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${collectionBalance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Balance: ₹{collectionBalance.toLocaleString('en-IN')}
            </span>
            <Button size="sm" onClick={() => setShowAddCollection(!showAddCollection)} className="text-xs">+ Add</Button>
          </div>
        </div>

        {collections.length > 0 && (
          <div className="space-y-2 mb-3">
            {collections.map((c: any) => (
              <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{new Date(c.payment_date).toLocaleDateString('en-GB')}</span>
                  <span className="font-medium">₹{c.amount_inr.toLocaleString('en-IN')}</span>
                  <span className="text-xs text-gray-400">{c.payment_method}</span>
                  {c.payment_reference && <span className="text-xs text-gray-400">Ref: {c.payment_reference}</span>}
                </div>
                <button onClick={() => deleteCollection(c.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
              </div>
            ))}
            <div className="text-right text-sm font-medium">Total: ₹{collectionsTotal.toLocaleString('en-IN')} / ₹{gstInvoiceTotal.toLocaleString('en-IN')}</div>
          </div>
        )}

        {showAddCollection && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Input type="date" value={newCollection.payment_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCollection({ ...newCollection, payment_date: e.target.value })} className="text-sm" />
              <Input type="number" placeholder="Amount ₹" value={newCollection.amount_inr || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCollection({ ...newCollection, amount_inr: parseFloat(e.target.value) || 0 })} className="text-sm" />
              <Select value={newCollection.payment_method} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewCollection({ ...newCollection, payment_method: e.target.value })} className="text-sm">
                <option value="">Payment Method</option>
                {INDIA_PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </Select>
              <Input placeholder="Reference" value={newCollection.payment_reference} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCollection({ ...newCollection, payment_reference: e.target.value })} className="text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveCollection} disabled={saving} className="text-xs">{saving ? 'Saving...' : 'Save'}</Button>
              <Button size="sm" variant="secondary" onClick={() => setShowAddCollection(false)} className="text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Remittance to IIW */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">🔄 Remittance to IIW (FlyRemit)</h4>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${remittanceBalance <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
              Balance: ${remittanceBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
            <Button size="sm" onClick={() => setShowAddRemittance(!showAddRemittance)} className="text-xs">+ Add</Button>
          </div>
        </div>

        {/* Customer PAN for FlyRemit */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-yellow-800">🇮🇳 Customer PAN (required for FlyRemit)</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Input value={customerPan} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" className="w-40 text-sm" />
            <Button size="sm" variant="secondary" onClick={savePan} className="text-xs">Save</Button>
          </div>
          {/* PAN Card Upload */}
          <div className="flex items-center gap-2">
            {panFileUrl ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-green-600">✅ PAN card uploaded</span>
                <button onClick={async () => {
                  try {
                    const token = getToken();
                    const rawUrl = panFileUrl || '';
                    const s3Key = rawUrl.startsWith('s3://')
                      ? rawUrl.replace(/^s3:\/\/[^/]+\//, '')
                      : rawUrl.replace(/https:\/\/[^/]+\//, '');
                    if (!s3Key) { alert('Invalid file URL'); return; }
                    const res = await fetch(`/api/files/presigned-url/${encodeURIComponent(s3Key)}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.success && data.data?.url) {
                      window.open(data.data.url, '_blank');
                    } else { alert('Unable to load file'); }
                  } catch (e) { alert('Error loading file'); }
                }} className="text-blue-600 hover:text-blue-800 underline">View</button>
                <button onClick={deletePanFile} className="text-red-500 hover:text-red-700">Delete</button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) { uploadPanFile(file); e.target.value = ''; }
                  }}
                  disabled={uploadingPan}
                  className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-yellow-100 file:text-yellow-700 hover:file:bg-yellow-200"
                />
                {uploadingPan && <span className="text-xs text-yellow-600">Uploading...</span>}
              </div>
            )}
          </div>
        </div>

        {remittances.length > 0 && (
          <div className="space-y-2 mb-3">
            {remittances.map((r: any) => (
              <div key={r.id} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{new Date(r.remittance_date).toLocaleDateString('en-GB')}</span>
                  <span className="font-medium">${r.amount_usd.toLocaleString()}</span>
                  <span className="text-xs text-gray-400">₹{r.amount_inr.toLocaleString('en-IN')} @ {r.conversion_rate}</span>
                  {r.remittance_ref && <span className="text-xs text-gray-400">Ref: {r.remittance_ref}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.status}</span>
                  {r.status === 'PENDING' && (
                    <button onClick={() => updateRemittanceStatus(r.id, 'COMPLETED')} className="text-xs text-blue-600 hover:text-blue-800 underline">Mark Complete</button>
                  )}
                </div>
              </div>
            ))}
            <div className="text-right text-sm font-medium">Remitted: ${remittedTotal.toLocaleString()} / ${packageCostUsd.toLocaleString()}</div>
          </div>
        )}

        {showAddRemittance && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input type="date" value={newRemittance.remittance_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, remittance_date: e.target.value })} className="text-sm" />
              <Input type="number" placeholder="USD Amount" value={newRemittance.amount_usd || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, amount_usd: parseFloat(e.target.value) || 0 })} className="text-sm" />
              <Input type="number" placeholder="INR Amount" value={newRemittance.amount_inr || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, amount_inr: parseFloat(e.target.value) || 0 })} className="text-sm" />
              <Input type="number" placeholder="Rate (INR/USD)" value={newRemittance.conversion_rate || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, conversion_rate: parseFloat(e.target.value) || 0 })} className="text-sm" />
              <Input placeholder="FlyRemit Ref" value={newRemittance.remittance_ref} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, remittance_ref: e.target.value })} className="text-sm" />
              <Input placeholder="Remarks" value={newRemittance.remarks} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRemittance({ ...newRemittance, remarks: e.target.value })} className="text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveRemittance} disabled={saving} className="text-xs">{saving ? 'Saving...' : 'Save'}</Button>
              <Button size="sm" variant="secondary" onClick={() => setShowAddRemittance(false)} className="text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* India Margin */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-semibold text-green-800 mb-2">🇮🇳 Wynder India Margin</h4>
        <div className="flex items-center gap-6 text-sm">
          <div><span className="text-gray-500">Handling Fee:</span> <span className="font-bold text-green-700">₹{gstSummary.handling_fee_inr?.toLocaleString('en-IN')}</span></div>
          <div><span className="text-gray-500">USD Equivalent:</span> <span className="font-medium">${gstSummary.handling_fee_usd?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
          <span className="text-green-600 text-xs">✅ Retained in India</span>
        </div>
      </div>

      {/* GST Liability */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800">🏛️ GST Liability</h4>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${gstOutstanding <= 0 ? 'text-green-600' : 'text-red-600'}`}>
              Outstanding: ₹{gstOutstanding.toLocaleString('en-IN')}
            </span>
            <Button size="sm" onClick={() => setShowAddGstGovt(!showAddGstGovt)} className="text-xs">+ Record Payment</Button>
          </div>
        </div>

        <div className="flex gap-4 text-sm mb-3">
          <div><span className="text-gray-500">Collected:</span> <span className="font-medium">₹{gstCollected.toLocaleString('en-IN')}</span></div>
          <div><span className="text-gray-500">Remitted:</span> <span className="font-medium text-green-600">₹{gstRemittedTotal.toLocaleString('en-IN')}</span></div>
        </div>

        {gstGovtPayments.length > 0 && (
          <div className="space-y-2 mb-3">
            {gstGovtPayments.map((g: any) => (
              <div key={g.id} className="flex items-center justify-between bg-gray-50 rounded p-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{g.period}</span>
                  <span className="font-medium">₹{g.total_amount.toLocaleString('en-IN')}</span>
                  {g.challan_number && <span className="text-xs text-gray-400">Challan: {g.challan_number}</span>}
                  {g.remittance_date && <span className="text-xs text-gray-400">{new Date(g.remittance_date).toLocaleDateString('en-GB')}</span>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${g.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{g.status}</span>
              </div>
            ))}
          </div>
        )}

        {showAddGstGovt && (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input placeholder="Period (e.g. March 2025)" value={newGstGovt.period} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGstGovt({ ...newGstGovt, period: e.target.value })} className="text-sm" />
              <Input type="number" placeholder="Amount ₹" value={newGstGovt.total_amount || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGstGovt({ ...newGstGovt, total_amount: parseFloat(e.target.value) || 0 })} className="text-sm" />
              <Input type="date" value={newGstGovt.remittance_date} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGstGovt({ ...newGstGovt, remittance_date: e.target.value })} className="text-sm" />
              <Input placeholder="Challan Number" value={newGstGovt.challan_number} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGstGovt({ ...newGstGovt, challan_number: e.target.value })} className="text-sm" />
              <Select value={newGstGovt.status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewGstGovt({ ...newGstGovt, status: e.target.value })} className="text-sm">
                <option value="PENDING">Pending</option>
                <option value="PAID">Paid</option>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveGstGovt} disabled={saving} className="text-xs">{saving ? 'Saving...' : 'Save'}</Button>
              <Button size="sm" variant="secondary" onClick={() => setShowAddGstGovt(false)} className="text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndiaOpsTab;
